import { useSearchActions } from '#/modules/search/contexts/SearchContext';

import type { SearchContextHeaderData } from './SearchContextHeaderData';
import { useSearchContextHeaderFetchPage } from './useSearchContextHeaderFetchPage';
import { useSearchContextHeaderInput } from './useSearchContextHeaderInput';

type Props = {
  currentObjectId: string;
};

export function useSearchContextHeader({ currentObjectId }: Props): {
  searchContext: SearchContextHeaderData | null;
} {
  const { setSearchPageIndex } = useSearchActions();

  const { searchContextInput, isSearching } = useSearchContextHeaderInput({
    currentObjectId,
  });

  useSearchContextHeaderFetchPage({ searchContextInput, isSearching });

  const isIOSMobileApp =
    typeof window !== 'undefined' &&
    window.navigator?.userAgent?.includes('Idealist Mobile App');

  if (!searchContextInput) {
    if (isIOSMobileApp && window && window?.history?.length > 1) {
      return {
        searchContext: {
          searchResultsUrl: null,
          searchResultsState: {},
          previousResult: null,
          previousResultPageIndex: null,
          nextResult: null,
          nextResultPageIndex: null,
          resultIndex: null,
          totalResults: null,
          setPageIndex: () => {},
        },
      };
    }
    return { searchContext: null };
  }

  const {
    currentResultIndex,
    pageIndex,
    resultsByPage,
    searchResultsUrl,
    searchResultsState,
    previousResult,
    previousResultPageIndex,
    nextResult,
    nextResultPageIndex,
  } = searchContextInput;

  const results = resultsByPage[pageIndex];
  const resultIndex =
    (results?.hitsPerPage ?? 0) * (results?.page ?? 0) + currentResultIndex;

  const searchContext = {
    searchResultsUrl,
    searchResultsState,
    previousResult,
    previousResultPageIndex,
    nextResult,
    nextResultPageIndex,
    resultIndex,
    totalResults: results?.nbHits ?? 0,
    setPageIndex: (newPageIndex: number) => setSearchPageIndex(newPageIndex),
  } satisfies SearchContextHeaderData;

  return { searchContext };
}
